import './DroletQuickCart.scss';

import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
    useCartContent,
    useCartContext,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart';

import { Button } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { CSSTransition } from 'react-transition-group';
import CartItem from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/CartItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import { QuickCartIcon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/QuickCart';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';
import viewCartIcon from '@progymedia/sbi-web-englander/src/images/englander-icons/View-Icon.svg';

function _QuickCart() {
    const [loading, setLoading] = useState(false);
    const { isQuickCartVisible, setQuickCartVisible, isQuickCartLoading } =
        useCartContext();
    const [cart] = useCartContent();
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const { t } = useTranslation('layout');

    const { quickCartCMS } = useStaticQuery(graphql`
        query {
            quickCartCMS: allProductsJson(
                filter: { code: { eq: "englander_panier-rapide" } }
            ) {
                nodes {
                    code
                    attributes {
                        content {
                            valuesMarkdownRemark {
                                en_US
                                fr_CA
                                en_CA
                            }
                        }
                    }
                }
            }
        }
    `);

    const cartHeaderMessage =
        quickCartCMS?.nodes[0]?.attributes?.content?.valuesMarkdownRemark[0][
            currentLocale
        ];

    useEffect(() => {
        if (isQuickCartVisible) {
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.documentElement.style.overflow = '';
        }
    }, [isQuickCartVisible]);

    useEffect(() => {
        setLoading(isQuickCartLoading);
    }, [isQuickCartLoading]);

    const navigateCloseCart = (to: string) => {
        setQuickCartVisible(false);
        navigateTranslated(to);
    };

    const totalCartPrice = cart?.prices?.grand_total?.value ?? 0;
    const freeShipping = 100;
    const hasFreeShipping = totalCartPrice - freeShipping >= 0;

    return (
        <>
            <CSSTransition
                in={isQuickCartVisible}
                timeout={300}
                classNames='pageOverlay'
                unmountOnExit
            >
                <div
                    className='pageOverlay'
                    onClick={() => setQuickCartVisible(false)}
                ></div>
            </CSSTransition>
            <CSSTransition
                in={isQuickCartVisible}
                timeout={300}
                classNames='cartPopupMenu'
                unmountOnExit
            >
                <div key='cartPopupMenu' className={`cartPopupMenu cartSide`}>
                    {
                        // basic loading overlay
                        loading && (
                            <div className='overlay cart-loading'>
                                <div className='loader'>{t('loading')}...</div>
                            </div>
                        )
                    }
                    <div className='qCartHeader'>
                        {/* maybe: pourrait aussi afficher des messages promo/importants ?  ex.: x$ avant shipping gratuit */}
                        <p className={`q-cart-title`}>
                            {t('boutik:quick-cart.title')}
                        </p>

                        <Button onClick={() => setQuickCartVisible(false)}>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </div>

                    <div className='q-cart-content'>
                        {cart?.items?.length ? (
                            <>
                                {
                                    // List Cart Items
                                    cart.items.map((item: any) => {
                                        return (
                                            <CartItem
                                                item={item}
                                                setLoading={setLoading}
                                                key={item!.id}
                                                className={'list'}
                                            />
                                        );
                                    })
                                }
                                <hr className='hr-line' />
                                <p className='subtotal'>
                                    <span className='subtotal-label'>
                                        Item subtotal
                                    </span>
                                    <span className='subtotal-price'>
                                        {LocalePrice(
                                            currentLocale,
                                            cart?.prices?.subtotal_excluding_tax
                                                ?.value
                                        )}
                                    </span>
                                </p>
                            </>
                        ) : (
                            // Empty Cart Notice
                            <div className='cart-empty'>
                                <p>{t('boutik:quick-cart.empty')}</p>
                            </div>
                        )}
                    </div>

                    <span
                        className='q-cart-continue-shopping'
                        onClick={() => setQuickCartVisible(false)}
                    >
                        {t('header.continue-shopping')}
                        <FontAwesomeIcon icon={faArrowRight} />
                    </span>

                    {cart?.total_quantity ? (
                        <div className='q-cart-footer'>
                            <Button
                                className='qc-checkout'
                                onClick={() => navigateCloseCart('/my-cart/')}
                                label={t('boutik:quick-cart.view-cart')}
                                iconRight={viewCartIcon}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </CSSTransition>
        </>
    );
}

export { _QuickCart as QuickCart, QuickCartIcon };
